import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Alert, Box, Chip, CircularProgress, IconButton, Menu, MenuItem, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { Star, ChevronDown, Timer, Workflow, Pin, Plus } from "lucide-react";
import { T, TYPE } from "../../user/complaints/shared";
import { pm, errorMessage } from "../api";
import { usePm } from "../PmContext";
import { has } from "../ui";
import { CreateBoardDialog } from "./BoardsTab";
import BoardCanvas from "./BoardCanvas";
import BoardBacklog from "./BoardBacklog";
import SprintPlanning from "./SprintPlanning";
import BoardReports from "./BoardReports";
import BoardSettings from "./BoardSettings";

// Views per board type — Scrum and Kanban are genuinely different flows.
const VIEWS = {
  scrum: [["active-sprint", "Active sprint"], ["backlog", "Backlog"], ["planning", "Sprint planning"], ["reports", "Sprint reports"], ["settings", "Board settings"]],
  kanban: [["kanban", "Kanban"], ["backlog", "Backlog"], ["metrics", "Flow metrics"], ["cfd", "CFD"], ["control", "Control chart"], ["aging", "Aging work"], ["settings", "Board settings"]],
};
const REPORT_TAB = { reports: "burndown", metrics: "cycle", cfd: "cfd", control: "control", aging: "aging" };

const BoardShell = ({ project, boardId, view }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [error, setError] = useState("");
  const [others, setOthers] = useState([]);
  const [switcher, setSwitcher] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const canCreate = has(project, "board.manage") && !project.archivedAt;

  const load = useCallback(async () => {
    try {
      setBoard(await pm.board(boardId));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Board not found or you do not have access"));
    }
  }, [boardId]);
  useEffect(() => {
    setBoard(null);
    load();
    pm.boards(project._id).then(setOthers).catch(() => {});
  }, [load, project._id]);

  const savePrefs = useCallback(async (patch) => {
    setBoard((b) => (b ? { ...b, prefs: { ...(b.prefs || {}), ...patch } } : b));
    try {
      await pm.setBoardPrefs(boardId, patch);
    } catch (err) {
      ctx.fail(err);
    }
  }, [boardId, ctx]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!board) return <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box>;
  const views = VIEWS[board.type].filter(([k]) => (k !== "planning" || board.permissions.includes("sprint.view")) && (!["reports", "metrics", "cfd", "control", "aging"].includes(k) || board.permissions.includes("report.view")));
  const current = views.some(([k]) => k === view) ? view : views[0][0];
  const go = (v) => navigate(`/projects/${project.key}/boards/${board._id}/${v}`);
  const Icon = board.type === "scrum" ? Timer : Workflow;
  const props = { board, reloadBoard: load, prefs: board.prefs || {}, savePrefs, project };

  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Icon size={20} color={board.type === "scrum" ? T.brand : "#0052CC"} />
        <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#172B4D", letterSpacing: "-0.01em" }}>{board.name}</Typography>
        {(others.length > 1 || canCreate) && (
          <>
            <IconButton size="small" onClick={(e) => setSwitcher(e.currentTarget)} aria-label="Switch board"><ChevronDown size={16} /></IconButton>
            <Menu anchorEl={switcher} open={Boolean(switcher)} onClose={() => setSwitcher(null)}>
              {others.map((b) => <MenuItem key={b._id} selected={b._id === board._id} onClick={() => { setSwitcher(null); navigate(`/projects/${project.key}/boards/${b._id}`); }}>{b.name} · {b.type}</MenuItem>)}
              {others.length > 1 && <MenuItem component={RouterLink} to={`/projects/${project.key}/boards`} onClick={() => setSwitcher(null)}>All boards…</MenuItem>}
              {canCreate && <MenuItem onClick={() => { setSwitcher(null); setCreateOpen(true); }}><Plus size={14} style={{ marginRight: 8 }} />Create board</MenuItem>}
            </Menu>
            <CreateBoardDialog open={createOpen} onClose={() => setCreateOpen(false)} project={project} onCreated={(b) => { setCreateOpen(false); navigate(`/projects/${project.key}/boards/${b._id}`); }} />
          </>
        )}
        <Chip size="small" label={board.type === "scrum" ? "Scrum" : "Kanban"} sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: "#EAECF0", color: "#344054" }} />
        {board.key && <Typography sx={{ fontSize: TYPE.micro, color: T.inkMuted }}>{board.key}</Typography>}
        {board.sourceFilter && <Chip size="small" variant="outlined" label={`Filter: ${board.sourceFilter.name}`} sx={{ height: 22, fontSize: "0.7rem" }} />}
        <Tooltip title={board.prefs?.favorite ? "Remove from favourites" : "Favourite"}>
          <IconButton size="small" onClick={() => savePrefs({ favorite: !board.prefs?.favorite })}><Star size={16} fill={board.prefs?.favorite ? "#F79009" : "none"} color={board.prefs?.favorite ? "#F79009" : T.inkMuted} /></IconButton>
        </Tooltip>
        <Tooltip title={board.prefs?.isDefault ? "Your default board for this project" : "Make this my default board"}>
          <IconButton size="small" onClick={() => savePrefs({ isDefault: !board.prefs?.isDefault })}><Pin size={15} color={board.prefs?.isDefault ? T.brand : T.inkMuted} /></IconButton>
        </Tooltip>
        {board.description && <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }} noWrap>{board.description}</Typography>}
      </Stack>
      <Tabs value={current} onChange={(_, v) => go(v)} variant="scrollable" scrollButtons="auto"
        sx={{
          mb: 2,
          minHeight: 40,
          borderBottom: "1px solid #DFE1E6",
          "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontSize: "0.875rem", fontWeight: 600, px: 2, minWidth: 0, color: "#6B778C", "&.Mui-selected": { color: "#0052CC" } },
          "& .MuiTabs-indicator": { bgcolor: "#0052CC", height: "2.5px" },
        }}>
        {views.map(([k, l]) => <Tab key={k} value={k} label={l} />)}
      </Tabs>
      {(current === "active-sprint" || current === "kanban") && <BoardCanvas key={board._id} {...props} />}
      {current === "backlog" && <BoardBacklog key={board._id} {...props} />}
      {current === "planning" && <SprintPlanning key={board._id} {...props} />}
      {REPORT_TAB[current] && <BoardReports key={board._id} board={board} initial={REPORT_TAB[current]} />}
      {current === "settings" && <BoardSettings {...props} />}
    </Box>
  );
};

export default BoardShell;
